import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  groupByOptions,
} from '@features/admin/reports/reportData';
import { toast } from 'react-toastify';
import {
  ReportGroupBy,
  TReportAverageLifetimeValueCohortAnalysisParams,
  TReportAverageLifetimeValueCohortAnalysisData,
} from '@src/types/reports';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ComposedChart,
} from 'recharts';
import dayjs from 'dayjs';
import { DateValueType, downloadFile } from '@lib/utils';
import { useReportTableColumns } from '@src/hooks/useReportTableColumns';
import {
  exportAverageLifetimeValueCohortAnalysis,
  getAverageLifetimeValueCohortAnalysis,
} from '@src/services/reports';

import { ReportFilteringSettings } from '../ReportFilteringSettings';
// import { toast } from 'react-toastify';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: TReportAverageLifetimeValueCohortAnalysisData;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;

    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col items-center min-w-[160px] gap-y-1"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#B6D72C] text-xl font-semibold">
          ${data?.average_lifetime_value?.toFixed(2)}
        </p>
        <p className="text-[#999999] text-[10px]">{data?.cohort_period}</p>
        <p className="text-white text-xs">Average Lifetime Value</p>
      </div>
    );
  }

  return null;
};

export const AverageLifetimeValueCohortAnalysis: FC = () => {
  const allowedGroupBy: ReportGroupBy[] = [
    ReportGroupBy.WEEK,
    ReportGroupBy.MONTH,
    ReportGroupBy.QUARTER,
    ReportGroupBy.YEAR,
  ];

  const [selectedGroupBy, setSelectedGroupBy] = useState<string>(
    ReportGroupBy.WEEK
  );
  const [usersDateRange, setUsersDateRange] = useState<DateValueType>(null);
  const [data, setData] = useState<TReportAverageLifetimeValueCohortAnalysisData[]>([]);
  const [cohortSize, setCohortSize] = useState<number>(0);
  const [totalAverageLtv, setTotalAverageLtv] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);

  const lifetimeGroupByOptions = groupByOptions.filter((option) =>
    allowedGroupBy.includes(option.value as ReportGroupBy)
  );

  const formatDate = (value?: Date | string | null): string | null => {
    if (!value) return null;
    return dayjs(value).format('YYYY-MM-DD');
  };

  const buildPayload = (): TReportAverageLifetimeValueCohortAnalysisParams => {
    const grouping = allowedGroupBy.includes(selectedGroupBy as ReportGroupBy)
      ? (selectedGroupBy as ReportGroupBy)
      : ReportGroupBy.WEEK;

    return {
      grouping,
      start_date: formatDate(usersDateRange?.startDate),
      end_date: formatDate(usersDateRange?.endDate),
    };
  };

  const { columns, tableData } =
    useReportTableColumns<TReportAverageLifetimeValueCohortAnalysisData>({
      data,
      getAccessField: (item) => item.cohort_period,
      getMainDataField: (item) => item.average_lifetime_value || 0,
      summaryRow: {
        average_lifetime_value: totalAverageLtv,
      },
      customColumns: [
        {
          title: 'AVERAGE LIFETIME VALUE, USD',
          className: 'text-right',
          render: (item) => (
            <p className="text-white">
              ${(item?.average_lifetime_value ?? 0).toFixed(2)}
            </p>
          ),
        },
      ],
      includeTotalsRow: true,
    });

  const handleRunReport = async () => {
    setIsLoading(true);
    try {
      const payload = buildPayload();
      const response = await getAverageLifetimeValueCohortAnalysis(payload);

      setData(response.data);
      setCohortSize(response.cohort_size);
      setTotalAverageLtv(response.total_average_ltv);
    } catch (error) {
      toast.error('Error fetching report');
      console.error('Error fetching report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  const handleExportReport = async () => {
    try {
      const response = await exportAverageLifetimeValueCohortAnalysis(
        buildPayload(),
        'csv'
      );

      downloadFile(response.data, 'average-lifetime-value-cohort-analysis');
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Average Lifetime Value (Cohort Analysis)
      </h1>
      <p className="text-gray-400 text-sm">
        Report includes only users who did at least one time payment
      </p>

      <Card className="p-6">
        <ReportFilteringSettings
          showGroupByFilter
          dateRangeLabel={
            <>
              Users
              <br />
              <span className="text-gray-400 text-xs">
                Take into Account only user <br /> who sign up in defined
                period, <br />
                keep empty to use all user
              </span>
            </>
          }
          dateRangeValue={usersDateRange}
          groupByOptions={lifetimeGroupByOptions}
          isLoading={isLoading}
          selectedGroupBy={selectedGroupBy}
          showPeriodFilter={false}
          onDateRangeChange={setUsersDateRange}
          onGroupByChange={setSelectedGroupBy}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="mb-4 text-sm text-gray-300">
          Cohort Size: <span className="text-white font-semibold">{cohortSize}</span>
        </div>
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <ComposedChart data={data}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="cohort_period"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="line" wrapperStyle={{ fontSize: '14px' }} />
              <Line
                activeDot={{ r: 6 }}
                dataKey="average_lifetime_value"
                dot={{ fill: '#B6D72C', r: 4 }}
                name="Average Lifetime Value"
                stroke="#B6D72C"
                strokeWidth={2}
                type="monotone"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex justify-end">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={handleExportReport}
            >
              Download CSV
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
